'use client'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { LIGHT_FIELD_CLASS, LIGHT_HELP_CLASS } from '@/components/santa-video/light-field'
import { OptionalTag } from '@/components/santa-video/optional-tag'

interface OrderListFieldProps {
  id: string
  label: string
  help?: string
  placeholder?: string
  values: string[]
  maxEntries: number
  /** Huecos que no se pueden quitar (el mínimo de `personalDetails`). */
  minEntries?: number
  maxLength: number
  addLabel: string
  removeLabel: string
  onChange: (values: string[]) => void
  /** Si viene, la lista es opcional y la leyenda lo dice. */
  optionalTag?: string
}

/** Una lista del formulario (hechos, regalos, gustos): inputs nativos y «+ añadir otro». */
export function OrderListField(props: OrderListFieldProps) {
  const { id, label, help, placeholder, values, maxEntries, minEntries = 0, maxLength, addLabel, removeLabel, onChange, optionalTag } = props
  const setAt = (index: number, value: string) => onChange(values.map((entry, i) => (i === index ? value : entry)))
  const removeAt = (index: number) => onChange(values.filter((_, i) => i !== index))

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-sm font-semibold text-stone-800">{label}<OptionalTag tag={optionalTag} /></legend>
      {help && <p className={LIGHT_HELP_CLASS}>{help}</p>}
      {values.map((value, index) => (
        // El índice como `key` es aceptable aquí: los inputs son controlados
        // (el valor sale de `values`, no del DOM), las listas no pasan de 5
        // entradas cortas y ninguna fila guarda estado propio. Quitar una fila
        // reutiliza el nodo de la siguiente con su valor ya correcto.
        <div className="flex gap-2" key={`${id}-${index}`}>
          <Input
            aria-label={`${label} ${index + 1}`}
            className={LIGHT_FIELD_CLASS}
            maxLength={maxLength}
            onChange={(event) => setAt(index, event.target.value)}
            placeholder={placeholder}
            value={value}
          />
          {values.length > minEntries && (
            <Button className="text-stone-600 hover:bg-stone-100 hover:text-red-700" onClick={() => removeAt(index)} size="sm" type="button" variant="ghost">
              {removeLabel}
            </Button>
          )}
        </div>
      ))}
      {values.length < maxEntries && (
        <Button
          className="self-start border-stone-300 bg-white text-stone-800 hover:bg-stone-50 dark:bg-white"
          onClick={() => onChange([...values, ''])}
          size="sm"
          type="button"
          variant="outline"
        >
          {addLabel}
        </Button>
      )}
    </fieldset>
  )
}
